import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Paperclip, X } from 'lucide-react';
import {
  SOURCE_MAX_FILES,
  TASK_IMPORT_MAX_TASKS,
  TASK_IMPORT_MAX_TEXT,
  type GeneratedTaskDraft,
  type Job,
} from '@allemnii-pulse/shared';
import { useDocument } from '../../lib/firestore.js';
import { api } from '../../lib/api.js';
import {
  SOURCE_FILE_ACCEPT,
  uploadErrorMessage,
  uploadSourceFile,
} from '../../lib/sourceUploads.js';
import { Button } from '../../components/ui/Button.js';
import { Label } from '../../components/ui/Label.js';
import { Textarea } from '../../components/ui/Textarea.js';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';

type SourceDoc = { name: string; storagePath: string };

/**
 * Imports a task list the user already has — pasted text, uploaded files, or both, in any shape
 * (bullets, a spreadsheet export, meeting notes, a board screenshot). The API structures it into
 * the same drafts task generation produces (POST .../tasks/import), and `onImported` hands them
 * to the review table; nothing is saved until the reviewed list is committed.
 */
export function ImportTasksDialog({
  projectId,
  open,
  onOpenChange,
  onImported,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (drafts: GeneratedTaskDraft[]) => void;
}) {
  const [text, setText] = useState('');
  const [sources, setSources] = useState<SourceDoc[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);
  useEffect(() => {
    if (job?.status === 'succeeded' && job.result) {
      setJobId(null);
      onImported((job.result as { tasks: GeneratedTaskDraft[] }).tasks);
      setText('');
      setSources([]);
      onOpenChange(false);
    } else if (job?.status === 'failed') {
      setError(job.error ?? 'The import failed. Please try again.');
      setJobId(null);
    }
    // onImported/onOpenChange are fresh closures each render; only a job update should fire this.
  }, [job]);

  async function handleAttach(files: File[]) {
    setError(null);
    if (files.length > SOURCE_MAX_FILES - sources.length) {
      setError(`Attach up to ${SOURCE_MAX_FILES} files.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          storagePath: await uploadSourceFile(projectId, 'tasks', f),
        })),
      );
      setSources((s) => [...s, ...uploaded]);
    } catch (err) {
      setError(uploadErrorMessage(err, 'Could not upload the file. Please try again.'));
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canImport) return;
    setSubmitting(true);
    setError(null);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(`/projects/${projectId}/tasks/import`, {
        text: text.trim(),
        storagePaths: sources.map((s) => s.storagePath),
      });
      setJobId(id);
    } catch {
      setError('Could not start the import. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const working = jobId !== null;
  const canImport = !uploading && !submitting && !working && (text.trim() !== '' || sources.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-lg">
      {/* Phones lean on the Dialog's own dvh-capped scroll; an inner 80vh one would ignore the
          on-screen keyboard and nest a second scroller. */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:max-h-[80vh] md:overflow-y-auto md:pr-1"
      >
        <DialogHeader className="pr-6">
          <DialogTitle>Import tasks</DialogTitle>
          <DialogDescription>
            {working
              ? 'Reading your task list…'
              : `Paste a task list or attach one, in any format — bullets, a spreadsheet export, meeting notes. You'll review the tasks before anything is created. Up to ${TASK_IMPORT_MAX_TASKS} tasks at a time.`}
          </DialogDescription>
        </DialogHeader>

        {working ? (
          <div className="h-1 overflow-hidden rounded-full bg-surface-2" aria-live="polite">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-accent" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-tasks-text">Task list</Label>
              <Textarea
                id="import-tasks-text"
                rows={8}
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={TASK_IMPORT_MAX_TEXT}
                placeholder={
                  '- Set up the client portal login (urgent, ~2 days)\n- Quote approval page — after login\n- Email notifications when a ticket changes status'
                }
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm">
                Files <span className="text-muted">(optional)</span>
              </span>
              {sources.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {sources.map((s) => (
                    <li
                      key={s.storagePath}
                      className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm"
                    >
                      <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-muted" />
                      <span className="flex-1 truncate">{s.name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${s.name}`}
                        onClick={() =>
                          setSources((list) => list.filter((x) => x.storagePath !== s.storagePath))
                        }
                        // Padding offset by a negative margin: a finger-sized hit area, same layout.
                        className="-m-2 p-2 text-muted hover:text-danger md:m-0 md:p-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SOURCE_FILE_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  if (files.length > 0) void handleAttach(files);
                }}
              />
              {sources.length < SOURCE_MAX_FILES && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-10 self-start md:h-7"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {uploading ? 'Uploading…' : 'Attach files'}
                </Button>
              )}
              <p className="text-xs text-muted">
                PDF, CSV, Markdown, text or images — up to {SOURCE_MAX_FILES}.
              </p>
            </div>
          </>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        {!working && (
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!canImport}>
              Import
            </Button>
          </DialogFooter>
        )}
      </form>
    </Dialog>
  );
}
